/**
 * 渐变填充（补齐 registry.ts 早先登记的占位工具 / DESIGN.md §6.2）。
 *
 * 数值权威在 engine/fill/gradient.ts（五种几何、线性/sRGB 插值空间，
 * GPU 着色器同一套公式）——这里只是把它接到应用层：色标（默认前景→
 * 背景，也可以是渐变编辑器给的自定义多色标）、目标图层与选区裁剪、
 * 混合模式/不透明度，落盘走 engine/fill/apply.ts 的统一填充通道
 * （与 fillSelectionWithColor 同一条路径，唯一区别是取色函数换成
 * 逐像素求值的渐变而不是常量）。
 */
import { applyFill } from '../engine/fill/apply';
import type { SeparableBlendMode } from '../gpu/shaders/blend/modes';
import { sampleGradient, type GradientKind, type GradientSpec, type GradientStop } from '../engine/fill/gradient';
import type { AppStore } from './store';

export interface GradientPoint {
  readonly x: number;
  readonly y: number;
}

/** 渐变编辑器里的一个色标：颜色与不透明度分开存，UI 上是两个独立控件。 */
export interface GradientStopInput {
  /** 0–1，沿渐变轴的位置。 */
  readonly offset: number;
  readonly color: { readonly r: number; readonly g: number; readonly b: number };
  /** 0–1。 */
  readonly alpha: number;
}

export interface GradientFillOptions {
  readonly kind: GradientKind;
  readonly reverse: boolean;
  /** 省略 = 前景色在起点、背景色在终点（旧默认，向后兼容）。 */
  readonly stops?: readonly GradientStopInput[];
  /** 整体不透明度 0–1，省略 = 1。 */
  readonly opacity?: number;
  /** 混合模式，省略/'normal' = 直接 source-over。 */
  readonly blendMode?: SeparableBlendMode;
  /** 仿色：打散色标之间的 8 位色阶断层。 */
  readonly dither?: boolean;
}

/**
 * 在活动 raster 图层上应用渐变：`from`→`to` 定义渐变轴（文档坐标）。
 * 有选区时只在选区内生效（含羽化调制），无选区时整个画布。
 * 一次调用=一条历史。返回是否有修改发生。
 */
export function fillGradientDocument(
  store: AppStore,
  from: GradientPoint,
  to: GradientPoint,
  options: GradientFillOptions,
): boolean {
  const document = store.document;
  if (document === null) return false;
  const target = store.activeRasterLayerId();
  if (target === null) return false;
  // 渐变轴长度为 0（单击未拖动）没有几何意义，视为无操作
  if (from.x === to.x && from.y === to.y) return false;

  const selection = store.selection;
  const bounds =
    selection !== null && !selection.isEmpty
      ? selection.contentBounds()
      : { x: 0, y: 0, width: document.size.width, height: document.size.height };
  if (bounds === null) return false;

  const stops: readonly GradientStop[] =
    options.stops !== undefined
      ? options.stops.map((s) => ({
          offset: s.offset,
          color: [s.color.r, s.color.g, s.color.b, Math.round(s.alpha * 255)] as const,
        }))
      : [
          { offset: 0, color: [store.foreground.r, store.foreground.g, store.foreground.b, 255] },
          { offset: 1, color: [store.background.r, store.background.g, store.background.b, 255] },
        ];
  const spec: GradientSpec = {
    kind: options.kind,
    from,
    to,
    stops,
    space: 'linear',
    reverse: options.reverse,
    ...(options.dither === undefined ? {} : { dither: options.dither }),
  };

  const transaction = store.beginPixelTransaction('渐变');
  const touched = applyFill(
    store.tiles,
    target,
    bounds,
    (x, y) => sampleGradient(spec, x, y),
    transaction,
    {
      ...(selection === null ? {} : { selection }),
      ...(options.opacity === undefined ? {} : { opacity: options.opacity }),
      ...(options.blendMode === undefined ? {} : { blendMode: options.blendMode }),
    },
  );
  if (touched.length === 0) return false;
  store.commitPixels('渐变', transaction.end());
  store.invalidatePixels(touched);
  return true;
}
